/**
 * Resource Allocation Matrix Template Form
 * Resource distribution across projects
 */
import { useState, useEffect, useRef } from "react";
import { downloadTemplatePDF } from "../utils/pdfGenerator";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Download, Sparkles, RotateCcw, Plus, Trash2 , Save, Clock , FileText} from "lucide-react";
import { RAISection, FollowupsSection, API, useDCTMFile, FileUp, FileDown } from "./shared";
import { useAuth, useSettings } from "../App";

const ResourceAllocationForm = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeSettings } = useSettings();
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [saving, setSaving] = useState(false);
  const [autoSave, setAutoSave] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [savedTemplateId, setSavedTemplateId] = useState(null);
  const autoSaveIntervalRef = useRef(null);
  const dctmFileInputRef = useRef(null);
  
  // Refs to track latest values for auto-save (avoids stale closures)
  const formDataRef = useRef(null);
  const savedTemplateIdRef = useRef(null);
  const editingSavedId = searchParams.get('saved');

  const getEmptyForm = () => ({
    company_name: '',
    department: '',
    period: 'Q1 2025',
    date: new Date().toISOString().split('T')[0],
    prepared_by: '',
    project_names: ['Project A', 'Project B', 'Project C'],
    resources: [{
      resource_name: '', role: '', project_1_allocation: 0, project_2_allocation: 0,
      project_3_allocation: 0, project_4_allocation: 0, project_5_allocation: 0,
      total_allocation: 0, available: 100, notes: ''
    }],
    rai_assignments: [],
    followups: []
  });

  const getSampleData = () => ({
    company_name: 'TechCorp Inc.',
    department: 'Engineering',
    period: 'Q1 2025',
    date: new Date().toISOString().split('T')[0],
    prepared_by: 'Resource Manager',
    project_names: ['Platform V2', 'Mobile App', 'API Gateway', 'Infrastructure', 'Support'],
    resources: [
      { resource_name: 'John Smith', role: 'Senior Developer', project_1_allocation: 50, project_2_allocation: 0, project_3_allocation: 30, project_4_allocation: 0, project_5_allocation: 10, total_allocation: 90, available: 10, notes: 'Tech lead Platform V2' },
      { resource_name: 'Jane Doe', role: 'Full Stack Developer', project_1_allocation: 40, project_2_allocation: 40, project_3_allocation: 0, project_4_allocation: 0, project_5_allocation: 10, total_allocation: 90, available: 10, notes: '' },
      { resource_name: 'Mike Wilson', role: 'DevOps Engineer', project_1_allocation: 20, project_2_allocation: 0, project_3_allocation: 10, project_4_allocation: 60, project_5_allocation: 10, total_allocation: 100, available: 0, notes: 'Fully allocated' },
      { resource_name: 'Sarah Chen', role: 'QA Engineer', project_1_allocation: 40, project_2_allocation: 30, project_3_allocation: 20, project_4_allocation: 0, project_5_allocation: 0, total_allocation: 90, available: 10, notes: 'Testing all major projects' },
      { resource_name: 'Alex Kumar', role: 'Junior Developer', project_1_allocation: 0, project_2_allocation: 70, project_3_allocation: 0, project_4_allocation: 0, project_5_allocation: 20, total_allocation: 90, available: 10, notes: 'Ramping up on mobile' },
      { resource_name: 'Lisa Park', role: 'Product Manager', project_1_allocation: 50, project_2_allocation: 30, project_3_allocation: 20, project_4_allocation: 0, project_5_allocation: 0, total_allocation: 100, available: 0, notes: 'Product owner' }
    ],
    rai_assignments: [
      { task: 'Weekly resource review', responsible: 'Resource Manager', accountable: 'Engineering Director', informed: 'Project Managers', due_date: '2025-01-15', status: 'Pending' }
    ],
    followups: [
      { item: 'Evaluate contractor for API Gateway', owner: 'HR Manager', due_date: '2025-01-20', priority: 'Medium', status: 'In Progress' }
    ]
  });

  const [formData, setFormData] = useState(getEmptyForm());
  
  // Keep refs in sync with state
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);
  
  useEffect(() => {
    savedTemplateIdRef.current = savedTemplateId;
  }, [savedTemplateId]);

  const loadSavedTemplate = async (id) => {
    try {
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      const response = await axios.get(`${API}/saved-templates/${id}`, { headers });
      if (response.data && response.data.data) {
        setFormData(response.data.data);
        setSavedTemplateId(id);
      }
    } catch (error) {
      console.error("Error loading saved template:", error);
    }
  };

  // Internal save function that accepts values directly (for use with refs)
  const saveDraftInternal = async (dataToSave, existingSavedId, isAutoSave = false) => {
    if (!isAutoSave) setSaving(true);
    try {
      const draftName = dataToSave.project_name || dataToSave.title || dataToSave.company_name || "Resource Allocation Matrix Draft";
      const payload = {
        name: draftName,
        template_type: "Resource Allocation Matrix",
        template_id: "resource-allocation",
        format: "DOCX",
        data: dataToSave
      };
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      
      if (existingSavedId) {
        const response = await axios.put(`${API}/saved-templates/${existingSavedId}`, payload, { headers });
        // Update version if returned from server
        if (response.data?.version) {
          setFormData(prev => ({ ...prev, version: response.data.version }));
        }
      } else {
        const response = await axios.post(`${API}/saved-templates`, payload, { headers });
        if (response.data?.id) {
          setSavedTemplateId(response.data.id);
        }
      }
      setLastSaved(new Date());
      if (!isAutoSave) setSaving(false);
      return true;
    } catch (error) {
      console.error("Error saving draft:", error);
      if (!isAutoSave) setSaving(false);
      return false;
    }
  };

  const handleSaveDraft = async (isAutoSave = false) => {
    const success = await saveDraftInternal(formData, savedTemplateId, isAutoSave);
    if (!isAutoSave) {
      if (success) {
        alert("Draft saved successfully!");
      } else {
        alert("Error saving draft.");
      }
    }
  };

  // Load saved template if editing
  useEffect(() => {
    if (editingSavedId) {
      loadSavedTemplate(editingSavedId);
    }
  }, [editingSavedId]);

  // Auto-save functionality - uses refs to avoid stale closure issues
  useEffect(() => {
    if (autoSave && user?.email) {
      autoSaveIntervalRef.current = setInterval(() => {
        const currentFormData = formDataRef.current;
        const currentSavedId = savedTemplateIdRef.current;
        if (currentFormData) {
          saveDraftInternal(currentFormData, currentSavedId, true);
        }
      }, 30000);
    } else {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    }
    return () => {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    };
  }, [autoSave, user?.email]);

  // DCTM Import/Export handlers
  const handleExportDCTM = () => {
    const { exportToDCTM } = require("./shared");
    exportToDCTM(formData, "resource-allocation", "Resource Allocation", user?.email || '');
  };

  const handleImportDCTM = async (file) => {
    const { importFromDCTM, validateDCTMTemplate } = require("./shared");
    const result = await importFromDCTM(file);
    
    if (!result.success) {
      alert(result.error);
      return;
    }
    
    // Validate template compatibility
    const validation = validateDCTMTemplate("resource-allocation", result.meta);
    if (validation.warning) {
      const proceed = window.confirm(`Warning: ${validation.warning}

Do you want to continue importing?`);
      if (!proceed) return;
    }
    
    // Increment version for imported data
    const currentVersion = parseFloat(result.data.version) || 1.0;
    const newVersion = (currentVersion + 0.1).toFixed(1);
    
    // Set form data with incremented version
    setFormData({ ...result.data, version: newVersion });
    setSavedTemplateId(null);
    
    alert(`Document imported successfully!
Original version: ${result.meta.documentVersion}
New version: ${newVersion}`);
  };


  const handleDownload = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setLoading(true);
    try {
      const response = await axios.post(`${API}/generate/resource-allocation`, { ...formData, created_by_email: user?.email || "" }, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", `Resource_Allocation_${formData.department.replace(/\s+/g, '_') || 'Team'}_${formData.period.replace(/\s+/g, '_')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error:", error);
      alert("Error generating document.");
    }
    setLoading(false);
  };

  const handleDownloadPDF = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setPdfLoading(true);
    try {
      await downloadTemplatePDF({
        title: formData.title || formData.project_name || formData.meeting_title || formData.company_name || "Resource Allocation Matrix",
        formData,
        settings: activeSettings || {},
        templateType: "Resource Allocation Matrix",
        templateId: "resource-allocation",
        createdBy: user?.email,
        selectedSetting: activeSettings?.id,
        allSettings: null
      });
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert(error.message || "Error generating PDF. Please try again.");
    }
    setPdfLoading(false);
  };

  const addProject = () => {
    if (formData.project_names.length < 5) {
      setFormData({ ...formData, project_names: [...formData.project_names, `Project ${formData.project_names.length + 1}`] });
    }
  };

  const updateProjectName = (index, value) => {
    const names = [...formData.project_names];
    names[index] = value;
    setFormData({ ...formData, project_names: names });
  };

  const removeProject = (index) => {
    if (formData.project_names.length > 1) {
      setFormData({ ...formData, project_names: formData.project_names.filter((_, i) => i !== index) });
    }
  };

  const addResource = () => {
    setFormData({
      ...formData,
      resources: [...formData.resources, {
        resource_name: '', role: '', project_1_allocation: 0, project_2_allocation: 0,
        project_3_allocation: 0, project_4_allocation: 0, project_5_allocation: 0,
        total_allocation: 0, available: 100, notes: ''
      }]
    });
  };

  const updateResource = (index, field, value) => {
    const resources = [...formData.resources];
    resources[index] = { ...resources[index], [field]: value };
    
    // Recalculate total and available
    let total = 0;
    for (let i = 1; i <= 5; i++) {
      total += parseFloat(resources[index][`project_${i}_allocation`]) || 0;
    }
    resources[index].total_allocation = total;
    resources[index].available = Math.max(0, 100 - total);
    
    setFormData({ ...formData, resources });
  };

  const removeResource = (index) => {
    setFormData({ ...formData, resources: formData.resources.filter((_, i) => i !== index) });
  };

  const roles = ['Senior Developer', 'Developer', 'Junior Developer', 'Tech Lead', 'DevOps Engineer', 'QA Engineer', 'Designer', 'Product Manager', 'Project Manager', 'Architect', 'Other'];

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/")} data-testid="back-btn"><ArrowLeft className="h-5 w-5" /></Button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Resource Allocation Matrix</h1>
                <p className="text-sm text-gray-500">Resource distribution across projects (% allocation)</p>
              </div>
            </div>
            <div className="flex gap-2 items-center flex-wrap justify-end">
              <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-md">
                <Clock className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-700">Auto-save</span>
                <Switch checked={autoSave} onCheckedChange={setAutoSave} data-testid="auto-save-toggle" />
              </div>
              <Button variant="outline" size="sm" onClick={() => setFormData(getSampleData())} data-testid="fill-sample-btn"><Sparkles className="h-4 w-4 mr-2" /> Sample</Button>
              <Button variant="outline" size="sm" onClick={() => { setFormData(getEmptyForm()); setSavedTemplateId(null); }} data-testid="reset-btn"><RotateCcw className="h-4 w-4 mr-2" /> Reset</Button>
            </div>
          </div>
          {lastSaved && (
            <div className="text-xs text-gray-500 mt-2 text-right flex items-center justify-end gap-3">
              {formData.version && <span className="bg-gray-100 px-2 py-0.5 rounded">v{formData.version}</span>}
              <span>Last saved: {lastSaved.toLocaleTimeString()}</span>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <Card>
          <CardHeader><CardTitle>Allocation Matrix</CardTitle></CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div><Label>Company *</Label><Input value={formData.company_name} onChange={(e) => setFormData({...formData, company_name: e.target.value})} placeholder="Company" /></div>
              <div><Label>Department *</Label><Input value={formData.department} onChange={(e) => setFormData({...formData, department: e.target.value})} placeholder="Engineering" /></div>
              <div><Label>Period</Label><Input value={formData.period} onChange={(e) => setFormData({...formData, period: e.target.value})} placeholder="Q1 2025" /></div>
              <div><Label>Date</Label><Input type="date" value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})} /></div>
              <div><Label>Prepared By</Label><Input value={formData.prepared_by} onChange={(e) => setFormData({...formData, prepared_by: e.target.value})} placeholder="Manager" /></div>
            </div>

            <Separator />

            <div>
              <Label className="font-semibold">Projects (up to 5)</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.project_names.map((name, idx) => (
                  <div key={idx} className="flex items-center gap-1 bg-blue-50 rounded-lg p-2">
                    <Input value={name} onChange={(e) => updateProjectName(idx, e.target.value)} className="w-32 h-8 text-sm" />
                    {formData.project_names.length > 1 && (
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeProject(idx)}><Trash2 className="h-3 w-3 text-red-500" /></Button>
                    )}
                  </div>
                ))}
                {formData.project_names.length < 5 && (
                  <Button variant="outline" size="sm" onClick={addProject}><Plus className="h-4 w-4 mr-1" /> Add Project</Button>
                )}
              </div>
            </div>

            <Separator />

            <div>
              <Label className="font-semibold text-lg">Resource Allocation (% per project)</Label>
              <p className="text-xs text-gray-500 mb-3">Enter percentage allocation for each resource. Total should not exceed 100%.</p>
              
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border p-2 text-left text-sm">Resource</th>
                      <th className="border p-2 text-left text-sm">Role</th>
                      {formData.project_names.map((name, idx) => (
                        <th key={idx} className="border p-2 text-center text-sm w-20">{name}</th>
                      ))}
                      <th className="border p-2 text-center text-sm w-16 bg-blue-100">Total</th>
                      <th className="border p-2 text-center text-sm w-16 bg-green-100">Avail</th>
                      <th className="border p-2 text-left text-sm w-32">Notes</th>
                      <th className="border p-2 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.resources.map((resource, idx) => (
                      <tr key={idx}>
                        <td className="border p-1">
                          <Input value={resource.resource_name} onChange={(e) => updateResource(idx, 'resource_name', e.target.value)} placeholder="Name" className="text-sm h-8" />
                        </td>
                        <td className="border p-1">
                          <Input value={resource.role} onChange={(e) => updateResource(idx, 'role', e.target.value)} placeholder="Role" className="text-sm h-8" />
                        </td>
                        {formData.project_names.map((_, pIdx) => (
                          <td key={pIdx} className="border p-1">
                            <Input 
                              type="number" 
                              min="0" 
                              max="100"
                              value={resource[`project_${pIdx + 1}_allocation`]} 
                              onChange={(e) => updateResource(idx, `project_${pIdx + 1}_allocation`, parseInt(e.target.value) || 0)} 
                              className="text-sm h-8 text-center"
                            />
                          </td>
                        ))}
                        <td className={`border p-1 text-center font-medium ${resource.total_allocation > 100 ? 'bg-red-100 text-red-700' : 'bg-blue-50'}`}>
                          {resource.total_allocation}%
                        </td>
                        <td className={`border p-1 text-center font-medium ${resource.available === 0 ? 'bg-yellow-100' : 'bg-green-50'}`}>
                          {resource.available}%
                        </td>
                        <td className="border p-1">
                          <Input value={resource.notes} onChange={(e) => updateResource(idx, 'notes', e.target.value)} placeholder="Notes" className="text-sm h-8" />
                        </td>
                        <td className="border p-1 text-center">
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeResource(idx)}><Trash2 className="h-3 w-3 text-red-500" /></Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Button variant="outline" size="sm" onClick={addResource} className="mt-3"><Plus className="h-4 w-4 mr-1" /> Add Resource</Button>
            </div>

            <Separator />
            <RAISection formData={formData} setFormData={setFormData} />
            <FollowupsSection formData={formData} setFormData={setFormData} />

            <div className="flex flex-col gap-3">
              <div className="flex gap-3">
                <Button onClick={() => handleSaveDraft(false)} disabled={saving || loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="save-draft-btn">
                <Save className="h-5 w-5 mr-2" /> {saving ? "Saving..." : (savedTemplateId ? "Update" : "Draft")}
              </Button>
              <Button onClick={handleDownload} disabled={loading || pdfLoading} className="flex-1" size="lg" data-testid="download-btn">
                <Download className="h-5 w-5 mr-2" /> {loading ? "Generating..." : "Download XLSX"}
            </Button>
              <Button onClick={handleDownloadPDF} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="download-pdf-btn">
                  <FileText className="h-5 w-5 mr-2" /> {pdfLoading ? "Generating..." : "PDF"}
              </Button>
              <input
                type="file"
                ref={dctmFileInputRef}
                accept=".dctm"
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    handleImportDCTM(e.target.files[0]);
                    e.target.value = '';
                  }
                }}
                className="hidden"
                data-testid="dctm-file-input"
              />
              <Button onClick={() => dctmFileInputRef.current?.click()} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="import-dctm-btn">
                <FileUp className="h-5 w-5 mr-2" /> Import
              </Button>
              <Button onClick={handleExportDCTM} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="export-dctm-btn">
                <FileDown className="h-5 w-5 mr-2" /> Export
              </Button>
              </div>
              {(loading || pdfLoading) && (
                <div className="flex items-center justify-center gap-2 py-2 px-4 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                  <span className="text-sm text-blue-700">
                    {loading ? "Generating DOCX document..." : "Generating PDF document..."}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default ResourceAllocationForm;
